import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useSubmitHssResponses } from "@/hooks/use-submit-hss-responses";
import { CampaignResponseRow } from "./campaign-response-row";
import { responseRequiresEvidence } from "../campaign-urgency";
import type { CampaignResponseValue } from "../campaign-urgency";
import type { Asset, AssetResponse, CampaignSummary } from "@amzn/hardware-self-service-api-model";
import type {
  ResponseInput,
  ScopeSummary,
  SubmitResponsesRequest,
} from "@/services/hardware-self-service/hardware-self-service.types";

interface CampaignResponseFormProps {
  campaign?: CampaignSummary;
  assets: Asset[];
  existingResponses: Record<string, AssetResponse>;
  login: string;
  currentUsername: string;
  scope?: ScopeSummary;
}

interface AssetRow {
  asset: Asset;
  key: string;
}

function buildRows(assets: Asset[]): AssetRow[] {
  return assets.map((asset, index) => ({ asset, key: `${asset.serialNumber || "asset"}-${index}` }));
}

function buildInitialResponses(rows: AssetRow[], existingResponses: Record<string, AssetResponse>) {
  const initial: Record<string, CampaignResponseValue> = {};
  for (const row of rows) {
    const existing = row.asset.serialNumber ? existingResponses[row.asset.serialNumber] : undefined;
    initial[row.key] = (existing?.response ?? "") as CampaignResponseValue;
  }
  return initial;
}

function buildInitialEvidence(rows: AssetRow[], existingResponses: Record<string, AssetResponse>) {
  const initial: Record<string, string> = {};
  for (const row of rows) {
    const existing = row.asset.serialNumber ? existingResponses[row.asset.serialNumber] : undefined;
    if (existing?.evidence) {
      initial[row.key] = existing.evidence;
    }
  }
  return initial;
}

function isResponseComplete(
  value: CampaignResponseValue | undefined,
  evidence: string | undefined
): boolean {
  if (!value) {
    return false;
  }
  if (responseRequiresEvidence(value)) {
    return Boolean(evidence?.trim());
  }
  return true;
}

export const CampaignResponseForm: FC<CampaignResponseFormProps> = ({
  campaign,
  assets,
  existingResponses,
  login,
  currentUsername,
  scope,
}) => {
  const { mutateAsync, isPending, error } = useSubmitHssResponses();
  const rows = useMemo(() => buildRows(assets), [assets]);
  const [responses, setResponses] = useState<Record<string, CampaignResponseValue>>(() =>
    buildInitialResponses(rows, existingResponses)
  );
  const [evidence, setEvidence] = useState<Record<string, string>>(() =>
    buildInitialEvidence(rows, existingResponses)
  );
  const [submitSucceeded, setSubmitSucceeded] = useState(false);

  const handleResponseChange = useCallback((rowKey: string, value: CampaignResponseValue) => {
    setSubmitSucceeded(false);
    setResponses((previous) => ({ ...previous, [rowKey]: value }));
    if (!responseRequiresEvidence(value)) {
      setEvidence((previous) => {
        if (!(rowKey in previous)) {
          return previous;
        }
        const { [rowKey]: _removed, ...rest } = previous;
        return rest;
      });
    }
  }, []);

  const handleEvidenceChange = useCallback((rowKey: string, value: string) => {
    setSubmitSucceeded(false);
    setEvidence((previous) => ({ ...previous, [rowKey]: value }));
  }, []);

  const buildRequest = useCallback((): SubmitResponsesRequest | null => {
    const campaignId = campaign?.id;
    if (!campaignId) {
      return null;
    }
    const responseInputs: ResponseInput[] = [];
    for (const row of rows) {
      const serialNumber = row.asset.serialNumber;
      const response = responses[row.key];
      if (!serialNumber || !response) {
        continue;
      }
      const trimmedEvidence = responseRequiresEvidence(response) ? evidence[row.key]?.trim() : undefined;
      responseInputs.push(
        trimmedEvidence
          ? { assetId: serialNumber, response, evidence: trimmedEvidence }
          : { assetId: serialNumber, response }
      );
    }
    return { campaignId, login, responses: responseInputs };
  }, [campaign, rows, responses, evidence, login]);

  const handleSave = useCallback(async () => {
    const request = buildRequest();
    if (!request) {
      return;
    }
    try {
      await mutateAsync(request);
      setSubmitSucceeded(true);
    } catch {
      // Failure is surfaced to the user via the mutation's `error` field; the
      // catch only prevents an unhandled rejection from this onClick handler.
    }
  }, [buildRequest, mutateAsync]);

  const handleAcknowledge = useCallback(async () => {
    const campaignId = campaign?.id;
    if (!campaignId) {
      return;
    }
    try {
      await mutateAsync({ campaignId, login, responses: [] });
      setSubmitSucceeded(true);
    } catch {
      // Failure is surfaced to the user via the mutation's `error` field; the
      // catch only prevents an unhandled rejection from this onClick handler.
    }
  }, [campaign, login, mutateAsync]);

  if (!campaign) {
    return (
      <MessageBanner type={MessageBannerType.Informational} isDismissible>
        {getString("campaigns.noActiveCampaignForm")}
      </MessageBanner>
    );
  }

  const isOnBehalf = login !== currentUsername;
  const isDelegatedAndLocked =
    login !== currentUsername && scope?.hasTargetSubmittedAResponse === true;
  const formattedEndDate = campaign.endDate
    ? new Date(campaign.endDate).toLocaleDateString()
    : getString("campaigns.unknown");

  if (assets.length === 0) {
    return (
      <Col gridGap="S300">
        <MessageBanner type={MessageBannerType.Informational} isDismissible>
          {isOnBehalf ? `${getString("campaigns.respondingOnBehalf", { login })} ` : null}
          {isOnBehalf
            ? getString("campaigns.noAssetsMessageOnBehalf")
            : getString("campaigns.noAssetsMessage")}{" "}
          {getString("campaigns.respondBy", { date: formattedEndDate })}
        </MessageBanner>

        {submitSucceeded && (
          <MessageBanner type={MessageBannerType.Success} isDismissible>
            {getString("campaigns.responsesSaved")}
          </MessageBanner>
        )}

        {error && (
          <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
            {getString("campaigns.saveError", { message: error.message })}
          </MessageBanner>
        )}

        <Row justifyContent="flex-end">
          <Button
            variant={ButtonVariant.Primary}
            disabled={isPending}
            onClick={handleAcknowledge}
            dataTestId="acknowledge-campaign-button"
          >
            {isPending ? getString("campaigns.saving") : getString("campaigns.acknowledge")}
          </Button>
        </Row>
      </Col>
    );
  }

  const isFormComplete =
    rows.length > 0 &&
    rows.every((row) => isResponseComplete(responses[row.key], evidence[row.key]));
  const respondedCount = rows.filter((row) => Boolean(responses[row.key])).length;

  return (
    <Col gridGap="S300">
      {isDelegatedAndLocked ? (
        <MessageBanner type={MessageBannerType.Warning} isDismissible={false}>
          {getString("campaigns.delegatedLocked")}
        </MessageBanner>
      ) : (
        <MessageBanner type={MessageBannerType.Informational} isDismissible>
          {isOnBehalf ? `${getString("campaigns.respondingOnBehalf", { login })} ` : null}
          {getString("campaigns.confirmDevices")}{" "}
          {getString("campaigns.respondBy", { date: formattedEndDate })}
        </MessageBanner>
      )}

      {submitSucceeded && (
        <MessageBanner type={MessageBannerType.Success} isDismissible>
          {getString("campaigns.responsesSaved")}
        </MessageBanner>
      )}

      {error && (
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {getString("campaigns.saveError", { message: error.message })}
        </MessageBanner>
      )}

      <Text color="neutral70">
        {getString("campaigns.respondedCounter", { respondedCount, total: rows.length })}
      </Text>

      <View
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(28rem, 1fr))",
          gap: "16px",
        }}
      >
        {rows.map((row) => (
          <CampaignResponseRow
            key={row.key}
            asset={row.asset}
            value={responses[row.key] ?? ""}
            evidence={evidence[row.key] ?? ""}
            disabled={isDelegatedAndLocked}
            onResponseChange={(value) => handleResponseChange(row.key, value)}
            onEvidenceChange={(value) => handleEvidenceChange(row.key, value)}
          />
        ))}
      </View>

      {!isDelegatedAndLocked && (
        <Row justifyContent="flex-end">
          <Button
            variant={ButtonVariant.Primary}
            disabled={!isFormComplete || isPending}
            onClick={handleSave}
            dataTestId="save-responses-button"
          >
            {isPending ? getString("campaigns.saving") : getString("campaigns.saveResponses")}
          </Button>
        </Row>
      )}
    </Col>
  );
};
